/**
 * The Documentation screen's app sections, and the query key that opens one — bata-ui#196 §4.
 *
 * The rail's app groups (`src/app/chrome/rail-layout.ts`, ADR 9023) each carry a Documentation
 * entry that lands on `/foundations/documentation?section=<app>`. The shell may import from a
 * feature and a feature may not import from the shell, so the vocabulary lives here, where the
 * screen that honours it lives, and the rail reads it. No imports: the rail pulls this file into
 * the chrome's bundle and must not pull the screen with it.
 *
 * A query key rather than a `#fragment` because the shell's navigation carries a path and a
 * query string (`useUrlState.navigate`) and has no fragment to carry.
 */

export const DOCUMENTATION_PATH = '/foundations/documentation';
export const DOCUMENTATION_SECTION_PARAM = 'section';

/** One section per rail app, in rail order. */
export const DOCUMENTATION_APP_SECTIONS = ['pricing', 'promotions', 'replenishment', 'compacting', 'health', 'management'] as const;
export type DocumentationAppSection = (typeof DOCUMENTATION_APP_SECTIONS)[number];

/** Pure parse: the section a search string names, or `null` for none or an unknown one. */
export function readDocumentationSection(search: string): DocumentationAppSection | null {
  const raw = new URLSearchParams(search).get(DOCUMENTATION_SECTION_PARAM)?.trim() ?? '';
  return (DOCUMENTATION_APP_SECTIONS as readonly string[]).includes(raw) ? (raw as DocumentationAppSection) : null;
}

/**
 * The query key that opens one screen's guide — `?guide=/inseason/workbench`, the route's declared
 * path (ADR 9044). The page header's «Read the guide» lands here: the guide's chapter open, the
 * guide expanded and its heading focused.
 */
export const DOCUMENTATION_GUIDE_PARAM = 'guide';

/** Pure parse: the guide path a search string names, or `null`. Validity is the screen's call. */
export function readDocumentationGuide(search: string): string | null {
  const raw = new URLSearchParams(search).get(DOCUMENTATION_GUIDE_PARAM)?.trim() ?? '';
  return raw.startsWith('/') ? raw : null;
}

/** The element id a guide's heading carries. */
export function documentationGuideId(path: string): string {
  return `documentation-guide${path.replace(/[^a-zA-Z0-9]+/g, '-')}`;
}

/** The element id a section is drawn under, so a test and the screen agree on it. */
export function documentationSectionId(section: DocumentationAppSection): string {
  return `documentation-app-${section}`;
}
